import React, { FC, MutableRefObject, ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, ReactState } from '../../../../React/Common/Contexts'
import { PaginationInfo } from '../../../../NonReact/Types/Types'
import { AdminFilters } from '../../Molecules/AdminFilters/AdminFilters'
import { PageTitle } from '../../Molecules/PageTitle/PageTitle'
import { DataTable, DataTableColumn } from '../../Molecules/DataTable/DataTable'
import { displayError } from '../../../../React/Common/Utils/Messages'
import {
    DirectoryListLanguage,
    GenericAdminFilterLanguage,
    GenericAdminListLanguage,
    Router as RouterLang,
    SidebarMenu as SidebarMenuLanguage
} from '../../../../../i18n'
import { DEFAULT_ROWS_PER_PAGE, UseAdminStatState, useAdminTableState } from '../../../Hooks/useAdminStatState'
import { useAdminListLegacyServletSwitch } from '../../../Hooks/useAdminListLegacyServletSwitch'
import { AdminBackendPermissionNames } from '../../../../React/Common/Constants/Permissions'
import { ServletCmds } from '../../../../NonReact/constants/ServletCommands'
import { IsVarEmpty, isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { UserInfo, useUserInfo } from '../../../Hooks/useUserInfo'
import { getDirectoryEntryList, getDirectoryEntryListForCME } from '../../../../React/Common/API/ItemsGetters'
import {
    DirectoryList as DirectoryListRows,
    DirectoryListItem,
    DirectoryListResponse,
    DirectoryListRow
} from '../../../../NonReact/Types/DirectoryListTypes'
import { ALL_VALUE, ANY_VALUE_STRING_NUMERIC } from '../../../../React/Common/Constants/DefaultValues'
import { EMPTY_STRING } from '../../../../React/Common/Constants/Separators'
import DirectoryModal from '../../../../React/DirectoryModal/Modal/DirectoryModal'
import { deleteDirectoryEntriesMany } from '../../../../NonReact/common/API/EntityAPIClient'

/**
 * True when the filter value is a concrete TelSys or partition selection (excludes [All] / Any placeholders).
 *
 * @param value - Raw telephony or partition option value from admin filters.
 * @returns Whether the value should be sent to the directory list API as a restricting parameter.
 */
const isConcreteTelSysOrPartitionFilterValue = (value: unknown): boolean =>
    isVarNotEmpty(value) && value !== ALL_VALUE && value !== ANY_VALUE_STRING_NUMERIC

/**
 * Routed Directory (directory entries) list view.
 *
 * @returns Directory list page content.
 */
export const DirectoryList: FC = (): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(RouterLang.NAME)
    language.loadBundle(DirectoryListLanguage.NAME)
    language.loadBundle(SidebarMenuLanguage.NAME)
    language.loadBundle(GenericAdminListLanguage.NAME)
    language.loadBundle(GenericAdminFilterLanguage.NAME)

    const [directoryList, setDirectoryList]: ReactState<Nullable<DirectoryListRows>> =
        useState<Nullable<DirectoryListRows>>()
    const {
        loading,
        setLoading,
        sort,
        setSort,
        filters,
        setFilters,
        pagination,
        setPagination,
        totalCount,
        setTotalCount
    }: UseAdminStatState = useAdminTableState()
    const [userInfo]: UserInfo = useUserInfo()
    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const [isDirectoryModalOpen, setDirectoryModalOpen]: ReactState<boolean> = useState<boolean>(false)
    const [selectedTelephoneNumber, setSelectedTelephoneNumber]: ReactState<Definable<string>> =
        useState<Definable<string>>()
    const [directoryModalViewOnly, setDirectoryModalViewOnly]: ReactState<boolean> = useState<boolean>(false)
    const [directoryModalTelSysID, setDirectoryModalTelSysID]: ReactState<Definable<number>> =
        useState<Definable<number>>()
    const [directoryModalPartIDInTelSys, setDirectoryModalPartIDInTelSys]: ReactState<Definable<number>> =
        useState<Definable<number>>()

    const listCmeId: Nullable<string> = (filters?.cme?.value ?? userInfo?.Scope?.CMEnvironmentID) as Nullable<string>
    const listSearch: string = filters?.search ?? EMPTY_STRING
    const listNoTelephonyServer: boolean = filters?.noTelephonyServer ?? false
    const listIncludeChildCmes: boolean = filters?.includeCmeChildren ?? true

    const listTelSysId: Definable<string | number> = isConcreteTelSysOrPartitionFilterValue(
        filters?.telephonyServer?.value
    )
        ? (filters?.telephonyServer?.value)
        : undefined
    const listPartIDInTelSys: Definable<string | number> =
        isVarNotEmpty(listTelSysId) && isConcreteTelSysOrPartitionFilterValue(filters?.partition?.value)
            ? (filters?.partition?.value)
            : undefined

    const hasConcretePartitionScope: boolean = isVarNotEmpty(listTelSysId) && isVarNotEmpty(listPartIDInTelSys)

    useAdminListLegacyServletSwitch(ServletCmds.ADMIN_DIRECTORY, filters, userInfo?.Scope?.CMEnvironmentID)

    /**
     * Fetches directory rows for the current admin filter scope (single partition or CME aggregate).
     *
     * @returns No return value.
     */
    const getDirectoryEntries = useCallback((): void => {
        if (IsVarEmpty(listCmeId)) {
            return
        }

        const scopedCmeId: string = listCmeId

        if (listNoTelephonyServer) {
            if (isMounted.current) {
                setDirectoryList([])
                setTotalCount(0)
            }
            return
        }

        setLoading((currentLoading: number): number => currentLoading + 1)

        const [currentPage, rowsPerPage]: PaginationInfo = pagination
        const searchQuery: Definable<string> = listSearch === EMPTY_STRING ? undefined : listSearch

        const listPromise: Promise<Nullable<DirectoryListResponse>> = hasConcretePartitionScope
            ? getDirectoryEntryList(
                  listTelSysId as string | number,
                  listPartIDInTelSys as string | number,
                  currentPage * rowsPerPage,
                  rowsPerPage,
                  sort,
                  searchQuery
              )
            : getDirectoryEntryListForCME(
                  scopedCmeId,
                  listIncludeChildCmes,
                  listTelSysId,
                  listPartIDInTelSys,
                  currentPage * rowsPerPage,
                  rowsPerPage,
                  sort,
                  searchQuery
              )

        listPromise
            .then((response: Nullable<DirectoryListResponse>): void => {
                if (isMounted.current) {
                    const rows: DirectoryListRows =
                        response?.Data?.Items?.map(
                            (item: DirectoryListItem, index: number): DirectoryListRow => ({
                                key: item.TelephoneNumber ?? `directory-row-${index}`,
                                Description: item.Description ?? EMPTY_STRING,
                                TelephoneNumber: item.TelephoneNumber ?? EMPTY_STRING,
                                TelSysID: item.TelSysID ?? listTelSysId ?? 0,
                                PartIDInTelSys: item.PartIDInTelSys ?? listPartIDInTelSys ?? 0
                            })
                        ) ?? []

                    setDirectoryList(rows)
                    setTotalCount(Number(response?.Data?.Total) || 0)
                }
            })
            .catch(displayError)
            .finally((): void => {
                if (isMounted.current) setLoading((currentLoading: number): number => currentLoading - 1)
            })
    }, [
        hasConcretePartitionScope,
        listCmeId,
        listIncludeChildCmes,
        listNoTelephonyServer,
        listSearch,
        listTelSysId,
        listPartIDInTelSys,
        pagination,
        sort,
        userInfo?.Scope?.CMEnvironmentID
    ])

    const refreshDirectoryList = useCallback((): void => {
        getDirectoryEntries()
    }, [getDirectoryEntries])

    const handleDirectoryModalRequestClose = useCallback((): void => {
        setDirectoryModalOpen(false)
        setSelectedTelephoneNumber(undefined)
        setDirectoryModalTelSysID(undefined)
        setDirectoryModalPartIDInTelSys(undefined)
        getDirectoryEntries()
    }, [getDirectoryEntries])

    const columns: DataTableColumn<DirectoryListRow, keyof DirectoryListRow>[] = useMemo(
        (): DataTableColumn<DirectoryListRow, keyof DirectoryListRow>[] => [
            {
                headerName: language.get(DirectoryListLanguage.DirectoryList_Column_Description),
                field: 'Description',
                key: 'directory-list-description',
                sortable: true,
                align: 'left'
            },
            {
                headerName: language.get(DirectoryListLanguage.DirectoryList_Column_TelephoneNumber),
                field: 'TelephoneNumber',
                key: 'directory-list-telephone',
                sortable: true,
                align: 'left'
            }
        ],
        [language]
    )

    useEffect(getDirectoryEntries, [getDirectoryEntries])

    return (
        <div className='directory-list admin-list-container'>
            <PageTitle title={RouterLang.PageTitle_directory} />
            <div className='directory-list-table-container admin-table-list-container'>
                <AdminFilters
                    localeText={{
                        cmeLabel: language.get(GenericAdminFilterLanguage.GenericAdminFilterLanguage_CME_LABEL),
                        telephonyServerLabel: language.get(
                            GenericAdminFilterLanguage.GenericAdminFilterLanguage_TELSYS_LABEL
                        ),
                        partitionLabel: language.get(
                            GenericAdminFilterLanguage.GenericAdminFilterLanguage_PARTITION_LABEL
                        ),
                        includeChildCmeLabel: language.get(
                            GenericAdminFilterLanguage.GenericAdminFilterLanguage_INCLUDE_CHILD_CMES_LABEL
                        ),
                        searchLabel: language.get(GenericAdminFilterLanguage.GenericAdminFilterLanguage_SEARCH_LABEL)
                    }}
                    onChange={setFilters}
                    optionsToDisplay={{
                        cme: true,
                        telephonyServer: true,
                        partition: true,
                        crmType: false,
                        scope: false,
                        codeType: false,
                        includeCmeChildren: true,
                        search: true,
                        includeRefresh: true
                    }}
                    onRefresh={getDirectoryEntries}
                />
                <DataTable<DirectoryListRow, keyof DirectoryListRow>
                    permissionName={AdminBackendPermissionNames.DIRECTORY}
                    localeText={{
                        addButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_Add),
                        viewButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_View),
                        changeButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_Change),
                        deleteButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_Delete),
                        selectionActive: (count: number): string =>
                            language.getInterpolated(GenericAdminListLanguage.GenericAdminList_Footer_Selected, count),
                        noRows: language.get(GenericAdminListLanguage.GenericAdminList_Footer_NoRows)
                    }}
                    columns={columns}
                    data={directoryList ?? []}
                    defaultRowsPerPageOptions={[10, 25, 50, 100]}
                    defaultRowsPerPage={DEFAULT_ROWS_PER_PAGE}
                    totalCount={totalCount}
                    isLoading={loading > 0}
                    paginationServer
                    sortServer
                    isSelectable
                    colIndexToTriggerClick={0}
                    toolbarAddDisabled={listNoTelephonyServer}
                    toolbarAddDisabledTitle={
                        listNoTelephonyServer
                            ? language.get(DirectoryListLanguage.DirectoryList_Add_Disabled_NoTelephonyFilter)
                            : EMPTY_STRING
                    }
                    onToolbarAddClick={(): void => {
                        setSelectedTelephoneNumber(undefined)
                        setDirectoryModalViewOnly(false)
                        setDirectoryModalTelSysID(isVarNotEmpty(listTelSysId) ? Number(listTelSysId) : undefined)
                        setDirectoryModalPartIDInTelSys(
                            isVarNotEmpty(listPartIDInTelSys) ? Number(listPartIDInTelSys) : undefined
                        )
                        setDirectoryModalOpen(true)
                    }}
                    onToolbarChangeClick={([selectedItem]: DirectoryListRow[]): void => {
                        setSelectedTelephoneNumber(selectedItem.TelephoneNumber)
                        setDirectoryModalViewOnly(false)
                        setDirectoryModalTelSysID(Number(selectedItem.TelSysID))
                        setDirectoryModalPartIDInTelSys(Number(selectedItem.PartIDInTelSys))
                        setDirectoryModalOpen(true)
                    }}
                    onToolbarViewClick={([selectedItem]: DirectoryListRow[]): void => {
                        setSelectedTelephoneNumber(selectedItem.TelephoneNumber)
                        setDirectoryModalViewOnly(true)
                        setDirectoryModalTelSysID(Number(selectedItem.TelSysID))
                        setDirectoryModalPartIDInTelSys(Number(selectedItem.PartIDInTelSys))
                        setDirectoryModalOpen(true)
                    }}
                    onToolbarDeleteClick={(selectedItems: DirectoryListRow[]): void => {
                        setLoading((currentLoading: number): number => currentLoading + 1)
                        deleteDirectoryEntriesMany(
                            selectedItems.map(
                                (
                                    row: DirectoryListRow
                                ): {
                                    TelSysID: string | number
                                    PartIDInTelSys: string | number
                                    TelephoneNumber: string
                                } => ({
                                    TelSysID: row.TelSysID,
                                    PartIDInTelSys: row.PartIDInTelSys,
                                    TelephoneNumber: row.TelephoneNumber
                                })
                            )
                        )
                            .then((): void => {
                                refreshDirectoryList()
                            })
                            .catch((error: Error): void => {
                                displayError(error)
                            })
                            .finally((): void => {
                                if (isMounted.current) setLoading((currentLoading: number): number => currentLoading - 1)
                            })
                    }}
                    onSortChange={setSort}
                    onRowsPerPageChange={(currentPage: number, newRowsPerPage: number): void =>
                        setPagination([currentPage, newRowsPerPage])
                    }
                    onPageChange={(newPage: number, rowsPerPage: number): void => setPagination([newPage, rowsPerPage])}
                />
                {isDirectoryModalOpen ? (
                    <DirectoryModal
                        key={`${selectedTelephoneNumber ?? 'new'}-${directoryModalTelSysID ?? 'any'}-${
                            directoryModalPartIDInTelSys ?? 'p'
                        }-${directoryModalViewOnly ? 'v' : 'e'}`}
                        filterTelSysID={directoryModalTelSysID}
                        filterPartitionPartIdInTelSys={directoryModalPartIDInTelSys}
                        selectedDirectory={selectedTelephoneNumber}
                        viewOnly={directoryModalViewOnly}
                        refreshAction={refreshDirectoryList}
                        onRequestClose={handleDirectoryModalRequestClose}
                    />
                ) : (
                    <></>
                )}
            </div>
        </div>
    )
}
